'use client';

import { getHouseOverrideCookieValue } from '@/utils/houseOverrideCookie';
import { houseOverrideAtom } from '@/atoms/houseOverride/houseOverrideAtom';
import { useEffect } from 'react';
import { useHasPermission } from '@/hooks/useHasPermission/useHasPermission';
import { useHydrateAtoms } from 'jotai/utils';
import { useSetAtom } from 'jotai';

type Props = Readonly<{
    /**
     * Server-read `houseOverride` cookie (from `next/headers` cookies()).
     * Hydrates the atom on first paint so SSR and the client agree — js-cookie is
     * unavailable during SSR and caused NavIdentity hydration mismatches.
     */
    initialOverride?: string;
}>;

/**
 * Seeds the houseOverrideAtom from the houseOverride cookie on initial app load.
 * Renders nothing — exists solely to bridge the cookie (persistence) and the
 * Jotai atom (reactive signal) on page load/refresh.
 *
 * Must be rendered inside AppContextProvider so useHasPermission is available.
 * Non-ATG-admin users are short-circuited; the atom stays null even if the cookie exists.
 */
export const HouseOverrideInitializer = ({ initialOverride = undefined }: Props): null => {
    const setHouseOverride = useSetAtom(houseOverrideAtom);
    const { isAtgAdmin } = useHasPermission();

    const shouldHydrateOverride = isAtgAdmin && initialOverride !== undefined && initialOverride !== '';

    // Always pass a Map so TypeScript hits the Map overload — a ternary `[[atom, value]] | []`
    // is inferred as a union that matches neither the empty-tuple nor iterable pair overloads.
    useHydrateAtoms(new Map(shouldHydrateOverride ? [[houseOverrideAtom, initialOverride]] : []));

    useEffect(() => {
        if (!isAtgAdmin) {
            return;
        }

        const cookieValue = getHouseOverrideCookieValue();
        if (cookieValue) {
            setHouseOverride(cookieValue);
            return;
        }

        // js-cookie reads document.cookie. That can be empty when the document request
        // still carried houseOverride (Next cookies() / injected Cookie header). Do not
        // wipe the SSR hydration in that case.
        if (!initialOverride) {
            setHouseOverride(null);
        }
    }, [initialOverride, isAtgAdmin, setHouseOverride]);

    return null;
};

HouseOverrideInitializer.displayName = 'HouseOverrideInitializer';
